import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyRestOptions } from 'shared/cex-sdk';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook } from '../utils';

const UPBIT_REST_BASE = 'https://api.upbit.com';
const UPBIT_WS_URL = 'wss://api.upbit.com/websocket/v1';
const UPBIT_REST_BOOK_DEPTH = 30;
const UPBIT_WS_BOOK_DEPTH = 15;
const UPBIT_MAX_CANDLES_PER_REQUEST = 200;
const UPBIT_MAX_HISTORY = 300;

export const upbitRestClient = proxyRestOptions.client!;

type UpbitNumber = number | string;

interface UpbitTicker {
  market?: string;
  trade_price: UpbitNumber;
  high_price: UpbitNumber;
  low_price: UpbitNumber;
  signed_change_rate: UpbitNumber;
  acc_trade_volume_24h: UpbitNumber;
  acc_trade_price_24h: UpbitNumber;
}

interface UpbitOrderbookUnit {
  ask_price: UpbitNumber;
  bid_price: UpbitNumber;
  ask_size: UpbitNumber;
  bid_size: UpbitNumber;
}

interface UpbitOrderbook {
  market?: string;
  timestamp: number;
  orderbook_units: UpbitOrderbookUnit[];
}

interface UpbitOrderbookInstrument {
  market: string;
  quote_currency: string;
  tick_size: string;
  supported_levels: string[];
}

export interface UpbitCandle {
  market?: string;
  code?: string;
  candle_date_time_utc: string;
  opening_price: UpbitNumber;
  high_price: UpbitNumber;
  low_price: UpbitNumber;
  trade_price: UpbitNumber;
  candle_acc_trade_volume: UpbitNumber;
}

interface UpbitTrade {
  market?: string;
  code?: string;
  trade_price: UpbitNumber;
  trade_volume: UpbitNumber;
  trade_timestamp: number;
  ask_bid: string;
}

type UpbitWsMessage =
  | ({ type: 'ticker'; code: string } & UpbitTicker)
  | ({ type: 'orderbook'; code: string } & UpbitOrderbook)
  | ({ type: 'trade'; code: string } & UpbitTrade)
  | ({ type: 'candle.1m'; code: string } & UpbitCandle)
  | { type?: never; error: { name?: string; message?: string } };

const UPBIT_CANDLE_PATHS: Partial<Record<IChartTimeframe, string>> = {
  '1m': '/v1/candles/minutes/1',
  '3m': '/v1/candles/minutes/3',
  '5m': '/v1/candles/minutes/5',
  '15m': '/v1/candles/minutes/15',
  '30m': '/v1/candles/minutes/30',
  '1h': '/v1/candles/minutes/60',
  '4h': '/v1/candles/minutes/240',
  '24h': '/v1/candles/days',
  '1d': '/v1/candles/days',
  '1w': '/v1/candles/weeks',
  '1M': '/v1/candles/months',
};

const asString = (value: UpbitNumber) => String(value);

const utcCandleTimestamp = (value: string): number => {
  const timestamp = Date.parse(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
  if (!Number.isFinite(timestamp)) {
    throw new Error(`Invalid Upbit candle UTC timestamp: ${value}`);
  }
  return timestamp;
};

export const buildUpbitMarketCode = (base: string, quote: string) =>
  `${quote.toUpperCase()}-${base.toUpperCase()}`;

export const normalizeUpbitCandles = (candles: UpbitCandle[]): KLine[] =>
  candles
    .map(
      candle =>
        [
          utcCandleTimestamp(candle.candle_date_time_utc),
          asString(candle.opening_price),
          asString(candle.high_price),
          asString(candle.low_price),
          asString(candle.trade_price),
          asString(candle.candle_acc_trade_volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const upbitPrecisionFromTick = (tick: string | undefined): number => {
  if (!tick || !Number.isFinite(Number(tick)) || Number(tick) <= 0) return 1;
  const normalized = tick.toLowerCase();
  if (normalized.includes('e-')) {
    const exponent = Number(normalized.split('e-')[1]);
    return Number.isFinite(exponent) ? exponent : 1;
  }
  const [, decimals = ''] = normalized.split('.');
  return decimals.replace(/0+$/, '').length;
};

export const buildUpbitSubscription = (market: string, ticket: string) => [
  { ticket },
  { type: 'ticker', codes: [market] },
  { type: 'orderbook', codes: [`${market}.${UPBIT_WS_BOOK_DEPTH}`] },
  { type: 'trade', codes: [market] },
  { type: 'candle.1m', codes: [market] },
  { format: 'DEFAULT' },
];

const readUpbitSocketPayload = async (data: unknown): Promise<string> => {
  if (typeof data === 'string') return data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data.text();
  if (
    data instanceof ArrayBuffer ||
    Object.prototype.toString.call(data) === '[object ArrayBuffer]'
  ) {
    return new TextDecoder().decode(data as ArrayBuffer);
  }
  if (ArrayBuffer.isView(data)) {
    return new TextDecoder().decode(new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
  }
  throw new Error('Unsupported Upbit WebSocket payload');
};

export class UpbitStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  priceTick = '0.1';
  asks: string[][] = [];
  bids: string[][] = [];

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildUpbitMarketCode(base, quote);
    this.displayDepth = orderbookDepth ?? UPBIT_REST_BOOK_DEPTH;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(this.asks, this.bids, this.selectedPrecision, this.displayDepth),
    );
  };

  publishTicker = (ticker: UpbitTicker) => {
    const rawLast = asString(ticker.trade_price);
    const changeNumber = Number(ticker.signed_change_rate) * 100;
    this.onTickerUpdate({
      baseVolume: asString(ticker.acc_trade_volume_24h),
      change: normalizeNumber(Number.isFinite(changeNumber) ? changeNumber : 0),
      close: rawLast,
      high: asString(ticker.high_price),
      last: rawLast,
      low: asString(ticker.low_price),
      quoteVolume: asString(ticker.acc_trade_price_24h),
    });
  };

  applyOrderbook = (book: UpbitOrderbook) => {
    this.asks = book.orderbook_units.map(unit => [
      asString(unit.ask_price),
      asString(unit.ask_size),
    ]);
    this.bids = book.orderbook_units.map(unit => [
      asString(unit.bid_price),
      asString(unit.bid_size),
    ]);
    this.publishOrderbook();
  };

  fetchInstrument = async () => {
    try {
      const response = await upbitRestClient.get<UpbitOrderbookInstrument[]>(
        `${UPBIT_REST_BASE}/v1/orderbook/instruments`,
        { params: { markets: this.pair } },
      );
      const instrument = response.data.find(item => item.market === this.pair);
      if (!instrument) throw new Error(`Upbit instrument ${this.pair} not found`);

      this.priceTick = instrument.tick_size;
      this.precision = upbitPrecisionFromTick(instrument.tick_size);
      this.selectedPrecision = this.precision;
      this.precisionUpdate(this.precision);
      if (this.asks.length || this.bids.length) this.publishOrderbook();
    } catch (error) {
      console.log('Upbit instrument error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await upbitRestClient.get<UpbitOrderbook[]>(
        `${UPBIT_REST_BASE}/v1/orderbook`,
        { params: { markets: this.pair } },
      );
      const book = response.data[0];
      if (!book?.orderbook_units.length) {
        throw new Error(`Upbit orderbook ${this.pair} is empty`);
      }
      this.applyOrderbook(book);
    } catch (error) {
      console.log('Upbit orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await upbitRestClient.get<UpbitTicker[]>(`${UPBIT_REST_BASE}/v1/ticker`, {
        params: { markets: this.pair },
      });
      const ticker = response.data[0];
      if (!ticker) throw new Error(`Upbit ticker ${this.pair} is empty`);
      this.publishTicker(ticker);
    } catch (error) {
      console.log('Upbit ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const path = UPBIT_CANDLE_PATHS[interval];
    if (!path) return [];

    const boundedLimit = Math.min(UPBIT_MAX_HISTORY, Math.max(1, limit));
    const byTimestamp = new Map<number, KLine>();
    let cursor = to > 0 ? to : Date.now();

    try {
      while (byTimestamp.size < boundedLimit) {
        const count = Math.min(UPBIT_MAX_CANDLES_PER_REQUEST, boundedLimit - byTimestamp.size);
        const response = await upbitRestClient.get<UpbitCandle[]>(`${UPBIT_REST_BASE}${path}`, {
          params: {
            market: this.pair,
            count,
            to: new Date(cursor).toISOString(),
          },
        });
        if (response.data.length === 0) break;

        const normalized = normalizeUpbitCandles(response.data);
        normalized.forEach(row => {
          if ((from <= 0 || row[0] >= from) && row[0] < cursor) {
            byTimestamp.set(row[0], row);
          }
        });

        const oldest = normalized[0]?.[0];
        if (!oldest || oldest >= cursor || response.data.length < count) break;
        cursor = oldest;
        if (from > 0 && cursor <= from) break;
      }

      return [...byTimestamp.values()]
        .sort((left, right) => left[0] - right[0])
        .slice(-boundedLimit);
    } catch (error) {
      console.log('Upbit klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    try {
      const response = await upbitRestClient.get<UpbitTrade[]>(
        `${UPBIT_REST_BASE}/v1/trades/ticks`,
        {
          params: {
            market: this.pair,
            count: Math.min(200, Math.max(1, limit)),
          },
        },
      );
      return response.data.map(trade => ({
        price: asString(trade.trade_price),
        quantity: asString(trade.trade_volume),
        timestamp: String(trade.trade_timestamp),
        side: trade.ask_bid.toUpperCase() === 'BID' ? 'buy' : 'sell',
      }));
    } catch (error) {
      console.log('Upbit trades error:', error);
      return [];
    }
  };

  connectWebSocket = ({ interval: _interval }: { interval: number }) => {
    this.closeWebSocket();

    const ws = new ReconnectingWebSocket(UPBIT_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.asks = [];
      this.bids = [];
      ws.send(
        JSON.stringify(
          buildUpbitSubscription(this.pair, `111-dashboard-upbit-${Date.now().toString(36)}`),
        ),
      );
    };

    ws.onmessage = async event => {
      try {
        const payload = await readUpbitSocketPayload(event.data);
        const message = JSON.parse(payload) as UpbitWsMessage;

        if ('error' in message && message.error?.message) {
          console.log('Upbit websocket subscription error:', message.error.message);
          return;
        }
        if (message.type === 'ticker') {
          this.publishTicker(message);
          return;
        }
        if (message.type === 'orderbook') {
          this.applyOrderbook(message);
          return;
        }
        if (message.type === 'trade') {
          this.onTradeUpdate({
            price: asString(message.trade_price),
            quantity: asString(message.trade_volume),
            timestamp: String(message.trade_timestamp),
            side: message.ask_bid.toUpperCase() === 'BID' ? 'buy' : 'sell',
          });
          return;
        }
        if (message.type === 'candle.1m') {
          const liveCandle = normalizeUpbitCandles([message])[0];
          if (liveCandle) this.klineUpdate(liveCandle);
        }
      } catch (error) {
        console.log('Upbit websocket error:', error);
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = () => [
    {
      value: this.priceTick,
      tick: this.precision,
      id: this.precision,
    },
  ];

  fetch = async () => {
    await Promise.all([this.fetchInstrument(), this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new UpbitStrategy(args);
    await strategy.fetch();
    return strategy;
  };
}
